import { useState, type FormEvent } from "react";
import { Navigate } from "react-router-dom";
import { KeyRound } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "../../services/api";
import { useAuth } from "../../store/AuthContext";
import { ErrorNotice } from "../../components/common/Primitives";

export default function RequiredPasswordPage() {
  const { t } = useTranslation();
  const { user, setUser } = useAuth();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  if (!user?.mustChangePassword) return <Navigate to="/" replace />;

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const password = String(form.get("password") || "");
    if (password !== form.get("confirmPassword")) {
      setError(t("Passwords do not match."));
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api("/auth/change-required-password", {
        method: "POST",
        body: JSON.stringify({
          currentPassword: form.get("currentPassword"),
          password,
        }),
      });
      setUser(null);
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="required-password-page">
      <section className="panel required-password-card">
        <KeyRound size={32} />
        <h1>{t("Choose your password")}</h1>
        <p>{t("Your temporary password must be replaced before continuing.")}</p>
        {error && <ErrorNotice message={error} />}
        <form onSubmit={submit}>
          <label>
            {t("Temporary password")}
            <input
              name="currentPassword"
              type="password"
              autoComplete="current-password"
              required
            />
          </label>
          <label>
            {t("New password")}
            <input
              name="password"
              type="password"
              autoComplete="new-password"
              minLength={12}
              maxLength={128}
              required
            />
          </label>
          <label>
            {t("Confirm password")}
            <input
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              minLength={12}
              maxLength={128}
              required
            />
          </label>
          <button className="button primary" disabled={busy}>
            {busy ? t("Saving…") : t("Save password")}
          </button>
        </form>
      </section>
    </main>
  );
}
